import { useWorkflowId } from "../../utils/misc";
import { RunInstanceStateToName } from "../../constants/workflow";
import DataViewsTable from "./DataViewsTable";
import Pagination from "../Common/Pagination";
import { useCallback, useEffect, useState } from "react";
import { DataViewsResponse } from "../../types/workflow";
import DataViewsHeader from "./DataViewsHeader";
import { useCreateOrUpdateDataViewsConfig, useGetDataViewsConfig, usePostDataViews } from "../../api/dataViews";
import { DataViewCondition, DataViewConfig, DataViewConfigResponse } from "../../types/dataViews";
import { ActionTypes, MatchingConditionOperator } from "../../constants/common";
import LoadingState from "../Common/LoadingState";

export type ViewsDataType = {
    id: string;
    data: Record<string, string>;
}

const DataViews = () => {
    const workflowId = useWorkflowId();
    const [dataViews, setDataViews] = useState<{
        data: DataViewsResponse[];
    } | null>(null);
    const [configResponse, setConfigResponse] = useState<DataViewConfigResponse | null>(null);
    const { mutate: getDataViewsConfig } = useGetDataViewsConfig();
    const { mutate: createOrUpdateDataViewsConfig } = useCreateOrUpdateDataViewsConfig();
    const { mutate: postDataViews, isPending: dataViewsLoading } = usePostDataViews();
    
    const [pageNo, setPageNo] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [filteredViewingData, setFilteredViewingData] = useState<ViewsDataType[] | null>(null);
    const [sortKey, setSortKey] = useState('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [viewingData, setViewingData] = useState<ViewsDataType[]>([]);
    const [columnsData, setColumnsData] = useState<Set<string>>(new Set());
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const [columnOrder, setColumnOrder] = useState<DataViewConfig[]>(configResponse?.config ? JSON.parse(configResponse?.config) : []);
    const [nodeIdToService, setNodeIdToService] = useState<Record<string, string>>({});
    const [overallFilterOperator, setOverallFilterOperator] = useState<MatchingConditionOperator>(MatchingConditionOperator.OR);
    const emptyCondition: DataViewCondition = {
        field_name: "",
        value: "",
        match_type: ActionTypes.IS,
    };
    const [conditions, setConditions] = useState<DataViewCondition[]>([{...emptyCondition}]);

    const getConvertedData = useCallback(() => {
        const allColumns = new Set<string>();
        const _nodeIdToService: Record<string, string> = {};
        const convertedData = dataViews?.data?.flatMap(dataView => {
            const sortedNodeRunInstances = dataView.node_run_instances.sort((a, b) => {
                const createdTimeA = new Date(a.created_time).getTime();
                const createdTimeB = new Date(b.created_time).getTime();
                return createdTimeA - createdTimeB
            });
            const nodeData: Record<string, string> = {
                'Run status': RunInstanceStateToName[dataView.run_instance.state],
                'Last step run': sortedNodeRunInstances?.[sortedNodeRunInstances.length - 1]?.node.name,
            };
            sortedNodeRunInstances.forEach(
                nodeRunInstance => Object.entries(nodeRunInstance.flat_map ? JSON.parse(nodeRunInstance.flat_map): {})?.forEach(([dataKey, dataValue]) => {
                    if(dataKey && dataValue) {
                        allColumns.add(`${dataKey}|${nodeRunInstance.node.id}|${nodeRunInstance.node.service}`);
                        nodeData[dataKey] = String(dataValue || '');
                    }
                    _nodeIdToService[nodeRunInstance.node.id] = nodeRunInstance.node.service;
                })
            );
            setNodeIdToService(_nodeIdToService);
            const dataToReturn = ({
                id: dataView.run_instance.id,
                data: nodeData,
            });
            return dataToReturn;
        })?.filter(viewData => Object.keys(viewData.data)?.length) || [];
        return {
            convertedData,
            allColumns
        }
    }, [dataViews?.data]);

    const applyFilters = () => {
        postDataViews(
            conditions?.length ?
                { payload: {
                    conditions: conditions,
                    operator: overallFilterOperator,
                }} : {},
            {
            onSuccess: (data) => {
                setDataViews(data);
            }
        })
    };

    const onSortKeyChange = (_sortKey: string, _sortOrder: 'asc' | 'desc') => {
        setSortKey(_sortKey);
        setSortOrder(_sortOrder);
        const sortColumnName = _sortKey.split('|').slice(0, -2).join("|");
        const { convertedData: dataToSort } = getConvertedData();
        const isDateOrTime = sortColumnName.toLowerCase().includes('time') || sortColumnName.toLowerCase().includes('date');
        const isDesc = _sortOrder === 'desc';
        const sortedData = dataToSort.sort((a, b) => {
            const dataA = a.data[sortColumnName];
            const dataB = b.data[sortColumnName];
            
            const isBlankA = dataA === null || dataA === undefined || dataA === '';
            const isBlankB = dataB === null || dataB === undefined || dataB === '';
            
            if (isBlankA && !isBlankB) {
                return isDesc ? 1 : -1;
            } else if (!isBlankA && isBlankB) {
                return isDesc ? -1 : 1;
            } else if (isBlankA && isBlankB) {
                return 0;
            }

            const sortDataA = isDateOrTime ? new Date(dataA).getTime() : dataA;
            const sortDataB = isDateOrTime ? new Date(dataB).getTime() : dataB;
            
            if (sortDataA > sortDataB) {
                return isDesc ? -1 : 1;
            } else if (sortDataB > sortDataA) {
                return isDesc ? 1 : -1;
            }
            return 0;
        });
        setViewingData(sortedData);
        setPageNo(1);
    }

    const onDataViewsUpdate = (_columnOrder: DataViewConfig[]) => {
        createOrUpdateDataViewsConfig({
            payload: {
                workflow: {
                    id: workflowId
                },
                config: JSON.stringify(_columnOrder)
            },
            configId: configResponse?.id,
        }, {
            onSuccess: (data) => {
                setConfigResponse(data);
                setColumnOrder(_columnOrder);
            },
        })
    }

    useEffect(() => {
        const columnsToAdd: string[] = [];
        const config: DataViewConfig[] = configResponse?.config ? JSON.parse(configResponse?.config) : [];
        const columnsPresent = config.map((column) => `${column.value}|${column.node_id || 'none'}|${nodeIdToService[column.node_id || ''] || 'none'}`);
        Array.from(columnsData)?.forEach(column => {
            if(!columnsPresent.includes(column)) {
                columnsToAdd.push(column);
            }
        });
        let _columnVisibility: Record<string, boolean> = {};
        config?.forEach(configValue => {
            _columnVisibility = {..._columnVisibility,
                [`${configValue.value}|${configValue.node_id || 'none'}|${nodeIdToService[configValue.node_id || ''] || 'none'}`]: configValue.is_set
            }
        });
        let _columnOrder: DataViewConfig[] = [...config];
        if(columnsToAdd?.length) {
            columnsToAdd?.forEach(column => {
                const serviceName = column.split("|").pop();
                const nodeId = column.split("|").slice(-2, -1).join("|");
                const columnName = column.split('|').slice(0, -2).join("|");
                _columnVisibility = {..._columnVisibility,
                    [`${columnName}|${nodeId || 'none'}|${serviceName}`]: true
                };
                _columnOrder.push({
                    is_set: true,
                    value: columnName,
                    ...(nodeId === 'none' ? {} : {node_id: nodeId}),
                    ...(nodeId === 'none' ? {} : {data_view_type: "node_response"}),
                });
            });
        }
        setColumnVisibility({..._columnVisibility});
        setColumnOrder([..._columnOrder]);
    }, [columnsData, configResponse?.config, nodeIdToService]);

    useEffect(() => {
        getDataViewsConfig(undefined, {
            onSuccess: (data) => {
                setConfigResponse(data)
            },
        })
        postDataViews({}, {
            onSuccess: (data) => setDataViews(data)
        })
    }, [getDataViewsConfig, postDataViews]);

    useEffect(() => {
        const startIndex = rowsPerPage * (pageNo - 1);
        const endIndex = startIndex + rowsPerPage;
        setFilteredViewingData(viewingData?.slice(startIndex, endIndex) ?? null);
    }, [pageNo, rowsPerPage, viewingData]);

    useEffect(() => {
        const { convertedData, allColumns } = getConvertedData();
        setViewingData([...convertedData]);
        setColumnsData(allColumns);
    }, [getConvertedData]);

    return (
        <div className="flex flex-col w-[calc(100%_-_240px)] bg-white">
            <div className="text-lg font-medium m-4">Data Views</div>
            <div className="overflow-hidden bg-white border border-solid border-lightGrey border-b-0 rounded-lg p-4 pb-0 flex-1 mx-4">
                <DataViewsHeader
                    columnVisibility={columnVisibility}
                    setColumnVisibility={setColumnVisibility}
                    columnOrder={columnOrder}
                    nodeIdToService={nodeIdToService}
                    overallFilterOperator={overallFilterOperator}
                    setOverallFilterOperator={setOverallFilterOperator}
                    columnsDetails={columnOrder.map(column => ({ field_name: column.value, node_id: column.node_id, service: nodeIdToService[column.node_id || ""] || "none" }))}
                    emptyCondition={emptyCondition}
                    conditions={conditions}
                    setConditions={setConditions}
                    applyFilters={applyFilters}
                    onDataViewsUpdate={onDataViewsUpdate}
                />
                {dataViewsLoading ?
                    <LoadingState />
                    :
                    <DataViewsTable
                        viewsData={filteredViewingData || []}
                        allColumns={Array.from(columnsData?.add('Run status|none|none').add('Last step run|none|none')) || []}
                        sortKey={sortKey}
                        sortOrder={sortOrder}
                        onSortKeyChange={onSortKeyChange}
                        columnOrder={columnOrder.map(column => `${column.value}|${column.node_id || 'none'}|${nodeIdToService[column.node_id || ''] || 'none'}`)}
                        columnVisibility={columnVisibility}
                    />
                }
            </div>
            <Pagination
                totalRows={dataViews?.data?.length || 0}
                pageNo={pageNo}
                setPageNo={setPageNo}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                showSmallerValues
                additionalClasses="justify-end px-3 border-t border-solid border-lightGrey"
            />
        </div>
    )
}

export default DataViews;